import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Button } from "@/components/ui/button";

// How long "Copied" / "Copy blocked" stays before the label comes back.
const RESET_MS = 2000;

const MESSAGES = { copied: "Copied", blocked: "Copy blocked" };

/**
 * A button that copies `value` and says what happened: "Copied", or —
 * when the browser refuses the clipboard (plain http, permissions) —
 * "Copy blocked", so a silent no-op never reads as success. The visible
 * text is the accessible name, so give each one on a page its own
 * ("Copy text", "Copy id"); the outcome is also announced politely.
 */
function CopyButton({ value, children, ...buttonProps }) {
  const [state, setState] = useState("idle");

  useEffect(() => {
    if (state === "idle") return undefined;
    const t = setTimeout(() => setState("idle"), RESET_MS);
    return () => clearTimeout(t);
  }, [state]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setState("copied");
    } catch {
      setState("blocked");
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={copy}
        {...buttonProps}
      >
        {MESSAGES[state] ?? children}
      </Button>
      <span role="status" className="sr-only">
        {MESSAGES[state] ?? ""}
      </span>
    </>
  );
}

CopyButton.propTypes = {
  value: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

export default CopyButton;
